import { Target, Brain, AlertTriangle, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const MistakesDetailPanel = ({ mistakesData, onNavigate }) => {
  const { t } = useTranslation();

  // Use REAL mistakes data
  const topicBreakdown = mistakesData?.topicBreakdown || [];
  const totalMistakes = mistakesData?.totalMistakes || 0;

  if (topicBreakdown.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl p-8 border-2 border-red-500/30 max-w-md mx-auto">
          <Brain className="w-20 h-20 text-red-500 mx-auto mb-4 animate-pulse" />
          <h3 className="font-space text-2xl font-black text-gray-900 mb-2">
            {t('dashboard.zeroState.mistakes.panelTitle', { 
              defaultValue: 'No Mistakes Yet!' 
            })}
          </h3>
          <p className="text-gray-600 font-medium mb-6">
            {t('dashboard.zeroState.mistakes.panelDescription', { 
              defaultValue: 'Take a quick placement test to find your grammar weak spots and start tracking your progress.' 
            })}
          </p>
          <button
            onClick={() => onNavigate && onNavigate('/practice/grammar')}
            className="group relative bg-red-500 text-white px-8 py-4 rounded-xl font-space font-bold text-lg hover:bg-red-600 transition-all duration-300 overflow-hidden flex items-center justify-center gap-2 mx-auto shadow-lg hover:shadow-xl"
          >
            <span className="relative z-10">
              {t('dashboard.zeroState.mistakes.panelCta', { 
                defaultValue: 'Start Grammar Practice' 
              })}
            </span>
            <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
            <div className="absolute inset-0 bg-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Summary Card */}
      <div
        className="group relative bg-black text-white rounded-2xl p-6 overflow-hidden border-[3px] border-black"
        style={{
          animation: 'slideInScale 0.5s ease-out',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
        }}
      >
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-red-500 opacity-10 rounded-full transform -translate-y-16 translate-x-16" />

        {/* Corner decorations */}
        <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-white/20" />
        <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-white/20" />

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <h3 className="font-space text-2xl font-black tracking-tight">{t('dashboard.mistakes.title')}</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-xl p-4 border border-white/20">
              <p className="text-xs text-white/60 mb-1 uppercase tracking-wider font-medium">{t('dashboard.mistakes.totalMistakes', { count: totalMistakes })}</p>
<p className="font-space text-4xl font-black text-red-400">{totalMistakes}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 border border-white/20">
              <p className="text-xs text-white/60 mb-1 uppercase tracking-wider font-medium">{t('dashboard.mistakes.topicsAffected', { count: topicBreakdown.length })}</p>
<p className="font-space text-4xl font-black">{topicBreakdown.length}</p>
            </div>
          </div>
        </div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </div>

      {/* Mistakes List */}
      <div className="space-y-3">
        {topicBreakdown.slice(0, 5).map((mistake, i) => {
          // Determine severity based on mistake count
          const severity = mistake.mistakes >= 8 ? 'high' : mistake.mistakes >= 4 ? 'medium' : 'low';
          const severityColors = {
            high: { bg: 'bg-red-50', border: 'border-red-500', badge: 'bg-red-500', text: 'text-red-700' },
            medium: { bg: 'bg-orange-50', border: 'border-orange-400', badge: 'bg-orange-500', text: 'text-orange-700' },
            low: { bg: 'bg-yellow-50', border: 'border-yellow-400', badge: 'bg-yellow-500', text: 'text-yellow-700' }
          };
          const colors = severityColors[severity];

          return (
            <div
              key={i}
              className={`group relative ${colors.bg} border-[3px] ${colors.border} rounded-xl p-5 transition-all duration-300 cursor-pointer hover:shadow-xl overflow-hidden`}
              style={{
                animation: `slideInScale 0.5s ease-out ${i * 0.05}s both`
              }}
            >
              {/* Corner decorations */}
              <div className={`absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 ${colors.border}`} />
              <div className={`absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 ${colors.border}`} />

              <div className="relative z-10">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h5 className={`font-space font-black text-lg ${colors.text}`}>{mistake.name}</h5>
                    <p className="text-sm text-gray-600 font-medium">{mistake.category}</p>
                  </div>
                  <div className={`w-14 h-14 ${colors.badge} rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 border-2 border-white/20`}>
                    <span className="text-white font-space font-black text-xl">{mistake.mistakes}</span>
                  </div>
                </div>
                <button
                  onClick={() => onNavigate && onNavigate('/lessons')}
                  className={`w-full bg-black text-white py-3 rounded-xl font-space font-bold text-sm hover:scale-105 transition-all duration-300 overflow-hidden relative group/btn border-2 border-black`}
                >
                  <span className="relative z-10">{t('dashboard.mistakes.practiceButton')}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-black opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300" />
                </button>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div
        className="bg-white rounded-2xl p-6 border-[3px] border-black/15"
        style={{
          animation: 'slideInScale 0.5s ease-out 0.3s both'
        }}
      >
        <h4 className="font-space text-lg font-black text-black mb-4 tracking-tight">{t('dashboard.quickActions.title')}</h4>
        <div className="space-y-2">
          <button
            onClick={() => onNavigate && onNavigate('/lessons')}
            className="group relative w-full bg-white border-[3px] border-black text-black py-3 rounded-xl font-bold hover:bg-black hover:text-white transition-all duration-300 overflow-hidden flex items-center justify-center gap-2"
          >
            <Target className="w-5 h-5 relative z-10" />
            <span className="relative z-10">{t('dashboard.mistakes.focusOnWeak')}</span>
            <div className="absolute inset-0 bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
          </button>
          <button
            onClick={() => onNavigate && onNavigate('/practice/grammar')}
            className="group relative w-full bg-white border-[3px] border-black text-black py-3 rounded-xl font-bold hover:bg-black hover:text-white transition-all duration-300 overflow-hidden flex items-center justify-center gap-2"
          >
            <Brain className="w-5 h-5 relative z-10" />
            <span className="relative z-10">{t('dashboard.mistakes.mixedReview')}</span>
            <div className="absolute inset-0 bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
          </button>
        </div>
      </div>
    </>
  );
};

export default MistakesDetailPanel;